import React from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import styles from './MainLayout.module.css';

interface IMainLayout {
  children: React.ReactNode;
  isLoading?: boolean;
  loadingText?: string;
  className?: string;
}

export const AnimatedLogo = () => (
  <svg
    width="192"
    height="107"
    viewBox="0 0 192 107"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`${styles['scale-breathing']}`}
  >
    <path
      d="M0 107.001V0.000976563H27.5V107.001H0Z"
      fill="url(#paint0_linear_194_1715)"
      className={styles['animate-dash']}
    />
    <path
      d="M37 78.001V46.001H68.5V78.001H37Z"
      fill="url(#paint1_linear_194_1715)"
      className={styles['animate-dash']}
    />
    <path
      d="M81.5 107.001V0.000976563H141.5C141.5 0.000976563 162.002 -0.000954628 176.5 14.5019C191.25 29.2565 191.5 49.501 191.5 49.501V107.001H165V49.501C165 49.501 165 40.5 159 34C153 27.5 141.5 27.001 141.5 27.001H108V107.001H81.5Z"
      fill="url(#paint2_linear_194_1715)"
      className={styles['animate-dash']}
    />
    <path
      d="M153 62.5C153 71.6127 145.389 79 136 79C126.611 79 119 71.6127 119 62.5C119 53.3873 126.611 46 136 46C145.389 46 153 53.3873 153 62.5Z"
      fill="url(#paint3_linear_194_1715)"
      className={styles['animate-dash']}
    />
    <defs>
      <linearGradient
        id="paint0_linear_194_1715"
        x1="158.5"
        y1="107"
        x2="-24"
        y2="-23"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#0FA620">
          <animate
            attributeName="stop-color"
            values="#0FA620;#0FA680;#0FA620"
            dur="3s"
            repeatCount="indefinite"
          />
        </stop>
        <stop offset="1" stopColor="#7BB7FB">
          <animate
            attributeName="stop-color"
            values="#7BB7FB;#7BFBB7;#7BB7FB"
            dur="3s"
            repeatCount="indefinite"
          />
        </stop>
      </linearGradient>
      <linearGradient
        id="paint1_linear_194_1715"
        x1="158.5"
        y1="107"
        x2="-24"
        y2="-23"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#0FA620">
          <animate
            attributeName="stop-color"
            values="#0FA620;#0FA680;#0FA620"
            dur="3s"
            repeatCount="indefinite"
          />
        </stop>
        <stop offset="1" stopColor="#7BB7FB">
          <animate
            attributeName="stop-color"
            values="#7BB7FB;#7BFBB7;#7BB7FB"
            dur="3s"
            repeatCount="indefinite"
          />
        </stop>
      </linearGradient>
      <linearGradient
        id="paint2_linear_194_1715"
        x1="158.5"
        y1="107"
        x2="-24"
        y2="-23"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#0FA620">
          <animate
            attributeName="stop-color"
            values="#0FA620;#0FA680;#0FA620"
            dur="3s"
            repeatCount="indefinite"
          />
        </stop>
        <stop offset="1" stopColor="#7BB7FB">
          <animate
            attributeName="stop-color"
            values="#7BB7FB;#7BFBB7;#7BB7FB"
            dur="3s"
            repeatCount="indefinite"
          />
        </stop>
      </linearGradient>
      <linearGradient
        id="paint3_linear_194_1715"
        x1="158.5"
        y1="107"
        x2="-24"
        y2="-23"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#0FA620">
          <animate
            attributeName="stop-color"
            values="#0FA620;#0FA680;#0FA620"
            dur="3s"
            repeatCount="indefinite"
          />
        </stop>
        <stop offset="1" stopColor="#7BB7FB">
          <animate
            attributeName="stop-color"
            values="#7BB7FB;#7BFBB7;#7BB7FB"
            dur="3s"
            repeatCount="indefinite"
          />
        </stop>
      </linearGradient>
    </defs>
  </svg>
);

export const MainLayout = ({
  children,
  isLoading = false,
  loadingText = 'Loading...',
  className = '',
}: IMainLayout) => {
  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 1.25, sm: 1.5 },
        mx: 'auto',
        width: '100%',
        minWidth: 0,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.paper',
            zIndex: 50,
          }}
        >
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <AnimatedLogo />
            <CircularProgress
              size={96}
              thickness={2}
              sx={{
                position: 'absolute',
                color: 'primary.main',
                opacity: 0.3,
              }}
            />
          </Box>
          <Typography
            variant="body2"
            sx={{ mt: 2, fontWeight: 500, color: 'text.secondary' }}
          >
            {loadingText}
          </Typography>
        </Box>
      )}
      {children}
    </Box>
  );
};

